'use client'
import React, { ReactElement, useContext } from 'react'
import WrapperElement from '@/components/elements/WrapperElement'
import CircleMark from '@/icons/font-awesome/sharp-light/circle-xmark.svg'
import { useDispatch } from 'react-redux'
import {
  resetFiltersExceptGuests,
  setLocationFilter,
  setMaximumPrice,
  setRating,
  setStars,
} from '@/components/ui/wizard/WizardActions'
import { useWizardSelector } from '@/components/ui/wizard/WizardState'
import { ConfigContext } from '@/components/common/ConfigContext'
import { LocationFilterElement, LocationFilterElementType } from '../../domain-config/domain-config'
import { Dispatch } from 'redux'
import { allFilters } from '@/lib/utils/configUtils'
import { useTranslations } from 'next-intl'

function createOptionsLocation(
  locationFilter: LocationFilterElement[],
  selectOptions: LocationFilterElement[],
  dispatch: Dispatch,
  t: (key: string) => string,
) {
  function removeFilter(value: string, type: LocationFilterElementType): void {
    dispatch(
      setLocationFilter(
        locationFilter.filter((filter) => !(filter.value === value && filter.type === type)),
      ),
    )
  }

  return locationFilter
    .map((filter) => selectOptions.find((o) => o.value === filter.value && o.type === filter.type)!)
    .map((filter) => ({
      ...filter,
      label: t(filter.label || filter.value),
      remove: () => removeFilter(filter.value, filter.type),
    }))
}

export default function SearchFilterRemovalElement(): ReactElement {
  const config = useContext(ConfigContext)!
  const dispatch = useDispatch()
  const rating = useWizardSelector((state) => state.rating)
  const stars = useWizardSelector((state) => state.stars)
  const locationFilter = useWizardSelector((state) => state.locationFilter)
  const maximumPrice = useWizardSelector((state) => state.maximumPrice)
  const selectOptions = allFilters(config)
  const t = useTranslations('SearchPage.Props')
  const tSearchPage = useTranslations('SearchPage')

  const optionRating = createOption(rating, tSearchPage(`Rating.${rating || ''}`), () =>
    dispatch(setRating(undefined)),
  )

  const optionStars = createOption(stars, tSearchPage(`Stars.${stars || ''}`), () =>
    dispatch(setStars(undefined)),
  )

  const optionMaximumPrice = createOption(maximumPrice, `≤ ${maximumPrice || ''}` || '', () =>
    dispatch(setMaximumPrice(undefined)),
  )

  const optionsLocation = createOptionsLocation(locationFilter, selectOptions, dispatch, t)

  const allFilterOptions = [
    ...optionsLocation,
    ...optionRating,
    ...optionStars,
    ...optionMaximumPrice,
  ]

  if (allFilterOptions.length === 0) {
    return <></>
  }

  return (
    <WrapperElement>
      <div className="mt-10 flex flex-col items-center">
        <div className="mb-5">
          <p className="font-semibold text-stone-600">{tSearchPage('filterRemovalTitle')}</p>
        </div>
        <div className="mb-3 flex flex-wrap justify-center gap-2">
          {allFilterOptions.map((filter) => (
            <div
              key={filter?.value}
              className="relative mb-2 mr-2 flex cursor-pointer items-center truncate rounded-md bg-primary-lightened px-2 py-1 pr-3 font-medium text-primary wd:text-sm wm:mb-2 wm:max-w-full wm:text-md"
              onClick={filter.remove}
            >
              <CircleMark className="mr-2 h-4 w-4 fill-primary" /> {filter.label}
            </div>
          ))}
        </div>
        <div>
          <div
            className=" relative mb-2 mr-2 flex cursor-pointer items-center truncate rounded-md bg-primary-lightened px-2 py-1 pr-3 font-medium text-primary wd:text-sm wm:mb-2 wm:max-w-full wm:text-md"
            onClick={() => dispatch(resetFiltersExceptGuests())}
          >
            <CircleMark className="mr-2 h-4 w-4 fill-primary" />
            {tSearchPage('deleteAllFiltersLabel')}
          </div>
        </div>
      </div>
    </WrapperElement>
  )
}

function createOption(value: string | undefined, label: string, remove: () => void) {
  return value ? [{ value, label, remove }] : []
}
